import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";

import { tableQueryOptions } from "@/web/lib/tanstack";
import { tables } from "@tycho/web-bindings";

import { EMPTY_PLEDGE_FORM_STATE, type PledgeFormState } from "@/web/components/styled/PledgeForm";

/** Shared field copy — import from here instead of overriding per surface. */
export const PLEDGE_MESSAGE_LABEL = "What do you love about this business? (optional)";
export const PLEDGE_MESSAGE_PLACEHOLDER = "Share a message of support...";
export const PLEDGE_CONSENT_LABEL = "Don't display my name or information publicly";

export type PledgeUserProfile = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/** Values used to pre-fill a pledge form (stored pledge and/or card payload). */
export type PledgeFormSeed = {
  amount?: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  supportiveMessage?: string | null;
  consentToDisplay?: boolean | null;
};

/** Anonymous return-visit fields on the TTW card recipient view. */
export type PledgeCardFallback = {
  amount?: number;
  supportiveMessage?: string | null;
  consentToDisplay?: boolean | null;
};

export function resolvePledgeConsentToDisplay(stored: boolean | null | undefined): boolean {
  return stored ?? true;
}

export function displayPledgeName(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

export function optionalPledgeName(value: string): string | undefined {
  return value.trim() || undefined;
}

/**
 * Build initial {@link PledgeFormState} from a stored pledge seed and the
 * viewer profile. When `exactStoredNames` is true (a `my*Interests` row
 * exists), name fields use the stored values exactly — including intentional
 * empties — and do not fall back to Clerk.
 */
export function buildPledgeFormState(
  seed: PledgeFormSeed,
  user: PledgeUserProfile | null | undefined,
  { exactStoredNames = false }: { exactStoredNames?: boolean } = {},
): PledgeFormState {
  return {
    amount: seed.amount,
    firstName: exactStoredNames
      ? (seed.firstName ?? "")
      : displayPledgeName(seed.firstName ?? user?.firstName),
    lastName: exactStoredNames
      ? (seed.lastName ?? "")
      : displayPledgeName(seed.lastName ?? user?.lastName),
    email: seed.email ?? user?.email ?? "",
    supportiveMessage: seed.supportiveMessage ?? "",
    consentToDisplay: resolvePledgeConsentToDisplay(seed.consentToDisplay),
  };
}

/** Seed from a `my_business_card_interests` row, with optional TTW card fallback. */
export function businessCardPledgeSeed(
  myInterest: {
    interestDollarAmount?: bigint | number;
    firstName?: string | null;
    lastName?: string | null;
    supportiveMessage?: string | null;
    consentToDisplay?: boolean;
  } | null,
  cardFallback?: PledgeCardFallback,
): { seed: PledgeFormSeed; exactStoredNames: boolean } {
  if (myInterest !== null) {
    return {
      exactStoredNames: true,
      seed: {
        amount:
          myInterest.interestDollarAmount === undefined
            ? undefined
            : Number(myInterest.interestDollarAmount),
        firstName: myInterest.firstName,
        lastName: myInterest.lastName,
        supportiveMessage: myInterest.supportiveMessage,
        consentToDisplay: myInterest.consentToDisplay,
      },
    };
  }

  return {
    exactStoredNames: false,
    seed: {
      amount: cardFallback?.amount,
      supportiveMessage: cardFallback?.supportiveMessage,
      // Absent stored consent defaults public via resolvePledgeConsentToDisplay —
      // aligned with EMPTY_PLEDGE_FORM_STATE and every other pledge surface.
      consentToDisplay: cardFallback?.consentToDisplay,
    },
  };
}

export function pledgeFormHasChanges(
  formState: PledgeFormState,
  initial: PledgeFormState | null,
  { trimNames = false }: { trimNames?: boolean } = {},
): boolean {
  if (!initial) return true;
  const firstName = trimNames ? displayPledgeName(formState.firstName) : formState.firstName;
  const lastName = trimNames ? displayPledgeName(formState.lastName) : formState.lastName;
  const initialFirstName = trimNames ? displayPledgeName(initial.firstName) : initial.firstName;
  const initialLastName = trimNames ? displayPledgeName(initial.lastName) : initial.lastName;
  return (
    formState.amount !== initial.amount ||
    firstName !== initialFirstName ||
    lastName !== initialLastName ||
    formState.email !== initial.email ||
    formState.supportiveMessage !== initial.supportiveMessage ||
    formState.consentToDisplay !== initial.consentToDisplay
  );
}

export function hadExistingPledge(initial: PledgeFormState | null): boolean {
  return initial?.amount !== undefined;
}

/** Common submit payload fields shared by every pledge surface. */
export function pledgeFormSubmitFields(formState: PledgeFormState, isSignedIn: boolean) {
  return {
    firstName: optionalPledgeName(formState.firstName),
    lastName: optionalPledgeName(formState.lastName),
    email: isSignedIn ? undefined : formState.email.trim().toLowerCase(),
    supportiveMessage: formState.supportiveMessage.trim() || undefined,
    consentToDisplay: formState.consentToDisplay,
  };
}

/** Signed-in viewers must wait for `my*Interests` before opening the pledge dialog. */
export function isPledgeInterestLoading(isSignedIn: boolean, isInterestLoading: boolean): boolean {
  return isSignedIn && isInterestLoading;
}

export function useMyBusinessCardInterest(cardId: string) {
  const myInterestsQuery = useQuery(tableQueryOptions(tables.myBusinessCardInterests));
  const myInterest =
    myInterestsQuery.data?.find((interest) => interest.cardId.toString() === cardId) ?? null;
  return { myInterest, isLoading: myInterestsQuery.isLoading };
}

/**
 * Dialog-local pledge form state: seed on open, track edits against the
 * snapshot, reset on close.
 */
export function usePledgeDialogForm() {
  const [formState, setFormState] = useState<PledgeFormState>(EMPTY_PLEDGE_FORM_STATE);
  const initialFormStateRef = useRef<PledgeFormState | null>(null);

  const seedForm = useCallback(
    (
      seed: PledgeFormSeed,
      user: PledgeUserProfile | null | undefined,
      options?: { exactStoredNames?: boolean },
    ) => {
      const initial = buildPledgeFormState(seed, user, options);
      setFormState(initial);
      initialFormStateRef.current = initial;
      return initial;
    },
    [],
  );

  const clearForm = useCallback(() => {
    setFormState(EMPTY_PLEDGE_FORM_STATE);
    initialFormStateRef.current = null;
  }, []);

  const hasChanges = useMemo(
    () => pledgeFormHasChanges(formState, initialFormStateRef.current),
    [formState],
  );

  return {
    formState,
    setFormState,
    initialFormStateRef,
    seedForm,
    clearForm,
    hasChanges,
  };
}
